if (!customElements.get('rs3gt-campaign')) {
  customElements.define('rs3gt-campaign', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.viewport = this.querySelector('[data-campaign-viewport]');
      this.canvas = this.querySelector('[data-campaign-canvas]');
      this.track = this.querySelector('[data-campaign-track]');
      this.columns = [...this.querySelectorAll('[data-campaign-column]')];
      if (!this.viewport || !this.track || !this.canvas) return;
      this.root = this.closest('rs7gt-launch');
      this.controller = new AbortController();
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.pointerQuery = matchMedia('(hover: hover) and (pointer: fine)');
      this.positions = this.columns.map(() => 0);
      this.factor = 0.35;
      this.visible = false;
      const options = { signal: this.controller.signal };
      this.viewport.addEventListener('scroll', () => {
        // A resize can clamp scrollTop before ResizeObserver runs. Retain the
        // last user position until the new geometry has been measured.
        if (this.viewport.clientWidth === this.measuredWidth && this.viewport.clientHeight === this.measuredHeight) {
          this.scrollProgress = this.scrollRange > 0 ? this.viewport.scrollTop / this.scrollRange : 0;
          this.panProgress = this.panRange > 0 ? this.viewport.scrollLeft / this.panRange : 0.5;
        }
        this.requestFrame();
      }, { ...options, passive: true });
      this.viewport.addEventListener('pointermove', event => {
        if (!this.enhanced || !this.pointerQuery.matches || event.pointerType !== 'mouse' || !this.panRange) return;
        const bounds = this.viewport.getBoundingClientRect();
        this.panTarget = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width)) * this.panRange;
        this.requestFrame();
      }, options);
      this.viewport.addEventListener('pointerleave', () => {
        if (this.panTarget == null) return;
        this.panTarget = this.panRange / 2;
        this.requestFrame();
      }, options);
      // Native horizontal touch, trackpad and keyboard scrolling keep ownership
      // until another mouse movement explicitly requests a pan.
      ['pointerdown', 'wheel', 'keydown'].forEach(name => this.viewport.addEventListener(name, () => {
        this.panTarget = null;
      }, { ...options, passive: true }));
      this.motionQuery.addEventListener('change', () => this.measure(), options);
      this.pointerQuery.addEventListener('change', () => { this.panTarget = null; }, options);
      this.root?.addEventListener('rs7gt:motionchange', () => this.measure(), options);
      this.root?.addEventListener('rs7gt:pagecoveragechange', () => this.requestFrame(), options);
      document.addEventListener('visibilitychange', () => this.requestFrame(), options);
      // Selecting a block in the editor must also reveal photos in the inner scroller.
      document.addEventListener('shopify:block:select', event => {
        if (this.dataset.designMode === 'true' && this.contains(event.target)) {
          event.target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
        }
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.viewport);
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.requestFrame();
      });
      this.intersectionObserver.observe(this);
      this.measure();
    }

    measure() {
      if (!this.isConnected || !this.controller) return;
      const progress = this.scrollProgress || 0;
      const panProgress = this.panProgress ?? 0.5;
      this.enhanced = !this.motionQuery.matches && this.dataset.designMode !== 'true';
      this.toggleAttribute('data-campaign-motion', this.enhanced);
      this.canvas.style.removeProperty('height');
      this.columns.forEach(column => column.style.removeProperty('transform'));
      const height = this.viewport.clientHeight;
      const naturalHeight = this.track.offsetHeight;
      // The track includes the staggered column offset in its bottom padding.
      // H - (1 + factor) * scrollMax = viewport height at the last photo.
      if (this.enhanced) {
        this.canvas.style.height = `${height + Math.max(0, naturalHeight - height) / (1 + this.factor)}px`;
      }
      this.scrollRange = Math.max(0, this.viewport.scrollHeight - height);
      this.panRange = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      this.measuredWidth = this.viewport.clientWidth;
      this.measuredHeight = height;
      this.panTarget = null;
      this.viewport.scrollTop = Math.max(0, Math.min(1, progress)) * this.scrollRange;
      this.viewport.scrollLeft = Math.max(0, Math.min(1, panProgress)) * this.panRange;
      this.positions.fill(this.enhanced ? -this.viewport.scrollTop * this.factor : 0);
      this.renderColumns();
      this.lastTime = 0;
      this.requestFrame();
    }

    renderColumns() {
      this.columns.forEach((column, index) => {
        if (this.enhanced) column.style.transform = `translate3d(0, ${this.positions[index].toFixed(2)}px, 0)`;
        else column.style.removeProperty('transform');
      });
    }

    requestFrame() {
      if (this.frame || !this.controller) return;
      this.frame = requestAnimationFrame(time => this.update(time));
    }

    update(time) {
      this.frame = 0;
      if (!this.enhanced || !this.visible || document.hidden || this.root?.sceneVisibility?.(this) === 0) {
        this.lastTime = 0;
        return;
      }
      const delta = this.lastTime ? Math.min(50, time - this.lastTime) : 16;
      this.lastTime = time;
      const target = -this.viewport.scrollTop * this.factor;
      const durations = [190, 300, 100, 150];
      let moving = false;
      this.positions = this.positions.map((position, index) => {
        const distance = target - position;
        if (Math.abs(distance) < 0.1) return target;
        moving = true;
        return position + distance * (1 - Math.exp(-delta / durations[index]));
      });
      this.renderColumns();
      if (this.panTarget != null) {
        const distance = this.panTarget - this.viewport.scrollLeft;
        if (Math.abs(distance) <= 1) {
          this.viewport.scrollLeft = this.panTarget;
          this.panTarget = null;
        } else {
          this.viewport.scrollLeft += Math.sign(distance) * Math.max(1, Math.abs(distance) * (1 - Math.exp(-delta / 160)));
          moving = true;
        }
      }
      if (moving) this.requestFrame();
      else this.lastTime = 0;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.panTarget = null;
      this.canvas?.style.removeProperty('height');
      this.columns?.forEach(column => column.style.removeProperty('transform'));
      this.removeAttribute('data-campaign-motion');
    }
  });
}
